import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Loader2, Plus, Trash2, Users, Briefcase, ChevronDown, Check, Mail,
} from 'lucide-react';
import { Card, CardContent } from '@/Components/ui/card';
import { Button } from '@/Components/ui/button';
import { Input } from '@/Components/ui/input';
import { Label } from '@/Components/ui/label';
import { Heading } from '@/Components/ui/typography';
import { cn } from '@/lib/utils';
import {
  listCompanyRecruiters,
  addCompanyRecruiter,
  setRecruiterAccess,
  removeCompanyRecruiter,
} from './recruitersApi';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const initialsFor = (email) => {
  const source = (email || '?').trim();
  const parts = source.split(/[.\s@_-]+/).filter(Boolean);
  const seed = parts.length > 1 ? `${parts[0][0]}${parts[1][0]}` : parts[0].slice(0, 2);
  return (seed || '?').toUpperCase();
};

/**
 * Settings → Recruiters. Companies maintain a roster of third-party
 * recruiters/vendors and control which job descriptions each one can access.
 * A recruiter can sit on the roster with zero jobs and be granted access any
 * time. Reached directly from the sidebar, or from a job's "Manage recruiters"
 * shortcut which deep-links with ?jobId=... (we pre-grant that job on add and
 * highlight it in each access list).
 */
const RecruitersSection = () => {
  const [searchParams] = useSearchParams();
  const focusJobId = searchParams.get('jobId') || '';

  const [recruiters, setRecruiters] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [deletingEmail, setDeletingEmail] = useState('');

  // Which recruiter's access panel is open, plus the in-progress checkbox set.
  const [expandedEmail, setExpandedEmail] = useState('');
  const [draftAccess, setDraftAccess] = useState(() => new Set());
  const [savingEmail, setSavingEmail] = useState('');

  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const jobsById = useMemo(() => {
    const map = new Map();
    for (const j of jobs) map.set(String(j.job_id), j.job_title || `Job ${j.job_id}`);
    return map;
  }, [jobs]);

  const focusJobTitle = focusJobId ? jobsById.get(String(focusJobId)) : '';

  const loadItems = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setIsLoading(true);
    setError('');
    try {
      const { recruiters: rows, jobs: jobRows } = await listCompanyRecruiters();
      if (!mountedRef.current) return;
      setRecruiters(rows);
      setJobs(jobRows);
    } catch (err) {
      const msg = err?.response?.data?.error || err.message || 'Could not load recruiters.';
      if (mountedRef.current) setError(msg);
    } finally {
      if (mountedRef.current && !silent) setIsLoading(false);
    }
  }, []);

  useEffect(() => { loadItems(); }, [loadItems]);

  const handleAdd = useCallback(async () => {
    setError('');
    const normalized = email.trim().toLowerCase();
    if (!normalized) {
      setError('Recruiter email is required.');
      return;
    }
    if (!EMAIL_REGEX.test(normalized)) {
      setError('Enter a valid email address.');
      return;
    }
    setIsSubmitting(true);
    try {
      // If we arrived from a job's shortcut, grant that job immediately.
      const res = await addCompanyRecruiter({
        recruiterEmail: normalized,
        jobIds: focusJobId ? [focusJobId] : [],
      });
      toast.success(
        res?.created ? 'Recruiter invited — credentials emailed' : 'Recruiter added'
      );
      setEmail('');
      await loadItems({ silent: true });
    } catch (err) {
      const msg = err?.response?.data?.error || err.message || 'Could not add recruiter.';
      setError(msg);
      toast.error(msg);
    } finally {
      if (mountedRef.current) setIsSubmitting(false);
    }
  }, [email, focusJobId, loadItems]);

  const handleRemove = useCallback(async (target) => {
    if (!target?.email) return;
    if (typeof window !== 'undefined' && !window.confirm(
      `Remove ${target.email}? They will lose access to all of your jobs (their account is not deleted).`
    )) {
      return;
    }
    setDeletingEmail(target.email);
    try {
      await removeCompanyRecruiter({ recruiterEmail: target.email });
      toast.success('Recruiter removed');
      await loadItems({ silent: true });
    } catch (err) {
      const msg = err?.response?.data?.error || err.message || 'Could not remove recruiter.';
      toast.error(msg);
    } finally {
      if (mountedRef.current) setDeletingEmail('');
    }
  }, [loadItems]);

  const openAccess = useCallback((rec) => {
    if (expandedEmail === rec.email) {
      setExpandedEmail('');
      return;
    }
    setExpandedEmail(rec.email);
    setDraftAccess(new Set((rec.job_ids || []).map(String)));
  }, [expandedEmail]);

  const toggleJob = useCallback((jobId) => {
    setDraftAccess((prev) => {
      const next = new Set(prev);
      const key = String(jobId);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const handleSaveAccess = useCallback(async (rec) => {
    setSavingEmail(rec.email);
    try {
      await setRecruiterAccess({
        recruiterEmail: rec.email,
        jobIds: Array.from(draftAccess),
      });
      toast.success('Access updated');
      setExpandedEmail('');
      await loadItems({ silent: true });
    } catch (err) {
      const msg = err?.response?.data?.error || err.message || 'Could not update access.';
      toast.error(msg);
    } finally {
      if (mountedRef.current) setSavingEmail('');
    }
  }, [draftAccess, loadItems]);

  const sortedRecruiters = useMemo(() => {
    return [...recruiters].sort((a, b) =>
      (a?.email || '').toLowerCase().localeCompare((b?.email || '').toLowerCase())
    );
  }, [recruiters]);

  return (
    <Card className="bg-white rounded-2xl shadow-md p-8 w-full">
      <CardContent className="p-0 space-y-6">
        {/* Header */}
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-sky-50 text-sky-600">
            <Users className="h-5 w-5" />
          </div>
          <div>
            <Heading level="h2" className="text-xl font-semibold text-foreground">
              Vendor recruiters
            </Heading>
            <p className="text-sm text-muted-foreground mt-1">
              Add third-party recruiters or agencies who source candidates for you, and choose
              exactly which job descriptions each one can access. New recruiters get an email
              invite with login credentials. Removing a recruiter revokes their access to your
              jobs (it does not delete their account).
            </p>
          </div>
        </div>

        {/* Deep-link banner */}
        {focusJobId ? (
          <div className="flex items-center gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-800">
            <Briefcase className="h-4 w-4 shrink-0" />
            <span>
              Assigning recruiters for{' '}
              <span className="font-semibold">{focusJobTitle || `Job ${focusJobId}`}</span>
              {' '}— adding a recruiter below grants this job automatically, or tick it in any
              recruiter's access list.
            </span>
          </div>
        ) : null}

        {/* Add form */}
        <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 sm:p-5">
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="flex-1">
              <Label htmlFor="recruiter-email">Recruiter email *</Label>
              <Input
                id="recruiter-email"
                type="email"
                placeholder="recruiter@agency.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
                disabled={isSubmitting}
              />
            </div>
            <Button
              type="button"
              onClick={handleAdd}
              disabled={isSubmitting}
              className="min-w-[160px]"
            >
              {isSubmitting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Plus className="mr-2 h-4 w-4" />
              )}
              Add recruiter
            </Button>
          </div>
          {error ? (
            <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          ) : null}
        </div>

        {/* List */}
        <div className="rounded-xl border border-slate-200 bg-white">
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
            <div className="text-sm font-semibold text-slate-900">
              Recruiters
              <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                {sortedRecruiters.length}
              </span>
            </div>
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : null}
          </div>

          {sortedRecruiters.length === 0 && !isLoading ? (
            <div className="px-4 py-10 text-center">
              <Users className="mx-auto mb-2 h-6 w-6 text-slate-300" />
              <p className="text-sm font-medium text-slate-700">No recruiters yet</p>
              <p className="mt-1 text-xs text-slate-500">
                Add a recruiter above to let them source candidates for your jobs.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {sortedRecruiters.map((rec) => {
                const isOpen = expandedEmail === rec.email;
                const jobCount = (rec.job_ids || []).length;
                return (
                  <li key={rec.email} className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-700">
                        {initialsFor(rec.email)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold text-slate-900">
                          {rec.email}
                        </div>
                        <div className="truncate text-xs text-slate-500">
                          {rec.company_name ? <span>{rec.company_name} · </span> : null}
                          {jobCount === 0
                            ? 'No job access yet'
                            : `${jobCount} job${jobCount === 1 ? '' : 's'}`}
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => openAccess(rec)}
                        className="gap-1.5"
                      >
                        <Briefcase className="h-3.5 w-3.5" />
                        Manage access
                        <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', isOpen && 'rotate-180')} />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemove(rec)}
                        disabled={deletingEmail === rec.email}
                        className="text-slate-500 hover:text-red-600"
                        title="Remove recruiter"
                      >
                        {deletingEmail === rec.email ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </div>

                    {/* Access panel */}
                    {isOpen ? (
                      <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50/70 p-3">
                        {jobs.length === 0 ? (
                          <p className="text-sm text-slate-500 px-1 py-2">
                            You don't have any jobs yet. Post a job first, then assign it here.
                          </p>
                        ) : (
                          <>
                            <div className="mb-2 px-1 text-xs font-medium text-slate-500">
                              Tick the jobs this recruiter can source for:
                            </div>
                            <div className="grid gap-1.5 sm:grid-cols-2 max-h-64 overflow-y-auto">
                              {jobs.map((j) => {
                                const jid = String(j.job_id);
                                const checked = draftAccess.has(jid);
                                const isFocus = focusJobId && jid === String(focusJobId);
                                return (
                                  <label
                                    key={jid}
                                    className={cn(
                                      'flex items-center gap-2 rounded-md border px-2.5 py-2 text-sm cursor-pointer transition-colors',
                                      checked ? 'border-sky-300 bg-sky-50' : 'border-slate-200 bg-white hover:bg-slate-50',
                                      isFocus && 'ring-2 ring-sky-300'
                                    )}
                                  >
                                    <input
                                      type="checkbox"
                                      className="h-4 w-4 accent-sky-600"
                                      checked={checked}
                                      onChange={() => toggleJob(jid)}
                                    />
                                    <span className="min-w-0 flex-1 truncate text-slate-800">
                                      {j.job_title || `Job ${jid}`}
                                    </span>
                                    {checked ? <Check className="h-3.5 w-3.5 text-sky-600 shrink-0" /> : null}
                                  </label>
                                );
                              })}
                            </div>
                            <div className="mt-3 flex items-center justify-end gap-2">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => setExpandedEmail('')}
                                disabled={savingEmail === rec.email}
                              >
                                Cancel
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                onClick={() => handleSaveAccess(rec)}
                                disabled={savingEmail === rec.email}
                              >
                                {savingEmail === rec.email ? (
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : null}
                                Save access
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Mail className="h-3.5 w-3.5" />
          Recruiters sign in at the recruiter portal with the email you add here.
        </p>
      </CardContent>
    </Card>
  );
};

export default RecruitersSection;
